import React from "react";

import { filter, flatMap, toNumber } from "lodash-es";
import { useTranslation } from "react-i18next";

import InputRadio from "@/components/InputRadio";
import useLocale from "@/hooks/useLocale";

import type { Field, PublicFormInstance } from "@/utils/form";
import type { Answer } from "@/utils/search";

interface AdminSurveyResultProps {
  forms: PublicFormInstance[];
  answers: Answer[];
}

const RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const AdminSurveyResult = ({ forms, answers }: AdminSurveyResultProps) => {
  const { t } = useTranslation();
  const locale = useLocale();

  const getAnswer = (field: Field) => {
    const answer = answers.find((answer) => answer.field_key === field.key);
    if (answer) {
      if (field.choices?.length) {
        const choice = field.choices.find(
          (choice) => choice.value == answer.value,
        );
        if (choice) {
          return choice.label[locale];
        }
      }
      return answer.value;
    }
    return "No answer";
  };

  const fields = filter(
    flatMap(forms, (form) => form.fields),
    (field) => !field.key.includes("Question_") && !field.hidden,
  );

  return (
    <section className="mt-10">
      {fields.map((field) => (
        <div key={field.key} className="mt-6 pb-6 border-b-2 border-lightgrey">
          {field.key === "A799E" ? (
            <div>
              <label className="checkbox flex mt-7 text-dark-blue text-xs">
                <input
                  type="checkbox"
                  name="marketing_opt_in"
                  checked
                  disabled
                />
                <p>
                  {t("signup.terms", {
                    terms: (
                      <a
                        href="https://fordevroadtrip.com/#rules-and-regs"
                        className="font-semibold underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("signup.termsLink")}
                      </a>
                    ),
                  })}
                </p>
              </label>

              <label className="checkbox flex mt-7 text-dark-blue text-xs">
                <input
                  type="checkbox"
                  checked={getAnswer(field) === "A"}
                  disabled
                  name="marketing_opt_in"
                />
                <span dangerouslySetInnerHTML={{ __html: t("signup.optIn") }} />
              </label>
            </div>
          ) : (
            <div>
              <label className="text-lg font-bold mb-4">
                {field.label[locale]}
              </label>
              {field.type === "radio" ? (
                <div>
                  {field.choices?.map((choice) => (
                    <InputRadio
                      key={choice.value}
                      label={choice.label[locale]}
                      value={choice.value}
                      name={field.key}
                      checked={getAnswer(field) === choice.value}
                      inline={field.choices?.length === 10}
                      className={
                        field.choices?.length === 10
                          ? "mt-4 text-center grow flex flex-col items-center justify-center"
                          : "mt-4"
                      }
                      required={!field.optional}
                      disabled
                    />
                  ))}
                </div>
              ) : field.type === "rating" ||
                (field.type === "choices" && field.choices?.length === 10) ? (
                <div className="flex">
                  {RATINGS.map((rating) => (
                    <InputRadio
                      key={rating}
                      label={rating.toString()}
                      value={rating.toString()}
                      name={field.key}
                      checked={toNumber(getAnswer(field)) === rating}
                      className="mt-4 text-center grow flex flex-col items-center justify-center"
                      inline
                      disabled
                    />
                  ))}
                </div>
              ) : field.type === "yesno" ? (
                <div>
                  <InputRadio
                    label={t("survey.yes")}
                    value="yes"
                    name={field.key}
                    className="mt-4"
                    disabled
                    checked={getAnswer(field) === "yes"}
                  />
                  <InputRadio
                    label={t("survey.no")}
                    value="no"
                    name={field.key}
                    className="mt-4"
                    disabled
                    checked={getAnswer(field) === "no"}
                  />
                </div>
              ) : (
                <p
                  className="w-full py-2 px-3 border-blue border-2 rounded-lg bg-white text-black"
                  dangerouslySetInnerHTML={{ __html: getAnswer(field) }}
                />
              )}
            </div>
          )}
        </div>
      ))}
    </section>
  );
};

export default AdminSurveyResult;
